import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { ChevronRight, ChevronsDownUp, ChevronsUpDown, FilePlus, FileText, GripVertical, MoreHorizontal, Trash } from 'lucide-react'
import { buildTree, canCreateSubPage, collectParentIds, flattenTree } from '../utils/pageTreeUtils'
import { ancestorIds, countDescendants } from '../utils/docSearch'
import { usePageReorder } from '../hooks/usePageReorder'
import { usePageRowDragDrop } from '../hooks/usePageRowDragDrop'
import type { PageDropIndicator } from '../hooks/usePageRowDragDrop'
import type { PageReorderEvent } from '../hooks/usePageReorder'
import type { PageTreeNode } from '../utils/pageTreeUtils'
import type { PageDocument } from '@/lib/db/rxdb/hooks/entities/pages'
import type { MemberDocument } from '@/lib/db/rxdb/schemas/member'
import { useMembers, usePageMutations } from '@/lib/db/rxdb/hooks'
import { useDateTimeFormatters } from '@/lib/i18n/useDateTimeFormatters'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Icon, normalizeIconName } from '@/components/app/pickers/icon-picker'
import { memberAvatarSrc } from '@/lib/utils/memberAvatar'
import { cn } from '@/lib/utils'

/** Indent per level; guides sit under the parent's chevron. */
const INDENT = 20
const ROW_PAD = 16
const GRID = 'grid grid-cols-[minmax(0,1fr)_60px] sm:grid-cols-[minmax(0,1fr)_110px_110px_60px]'

interface DocsOutlineTableProps {
  pages: Array<PageDocument>
  /** A page to scroll to and highlight, opening its ancestors (search "reveal in tree"). */
  revealId: string | null
  onOpen: (page: PageDocument) => void
  onCreatePage: (parentId?: string) => void
}

/**
 * Docs as an outline table (design 4c): expandable rows with indent guides,
 * how many pages sit inside each, last edit and owner. Rows drag to reorder
 * or re-parent, like the sidebar tree.
 */
export function DocsOutlineTable({ pages, revealId, onOpen, onCreatePage }: DocsOutlineTableProps) {
  const { t } = useTranslation('projects')
  const { deletePage } = usePageMutations()
  const { members } = useMembers()
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set())
  const [flashId, setFlashId] = useState<string | null>(null)
  const rowRefs = useRef(new Map<string, HTMLDivElement>())

  const { handleReorder, draggingId, setDraggingId, dropIndicator, setDropIndicator } = usePageReorder(pages)

  const tree = useMemo(() => buildTree(pages), [pages])
  const rows = useMemo(() => flattenTree(tree, 0, collapsedIds), [tree, collapsedIds])
  const parentIds = useMemo(() => collectParentIds(tree), [tree])
  const inside = useMemo(() => countDescendants(pages), [pages])
  const memberByUser = useMemo(() => new Map(members.map((m) => [m.userId, m])), [members])
  const allCollapsed = parentIds.size > 0 && [...parentIds].every((id) => collapsedIds.has(id))

  const toggle = useCallback((id: string) => {
    setCollapsedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // Reveal: open the path down to the page, then scroll to it and flash it.
  useEffect(() => {
    if (!revealId) return
    const ancestors = ancestorIds(pages, revealId)
    setCollapsedIds((prev) => {
      if (!ancestors.some((id) => prev.has(id))) return prev
      const next = new Set(prev)
      for (const id of ancestors) next.delete(id)
      return next
    })
    setFlashId(revealId)
    const frame = requestAnimationFrame(() => {
      rowRefs.current.get(revealId)?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    })
    const timer = setTimeout(() => setFlashId(null), 1600)
    return () => {
      cancelAnimationFrame(frame)
      clearTimeout(timer)
    }
  }, [revealId, pages])

  const handleDelete = useCallback(
    async (pageId: string) => {
      try {
        await deletePage(pageId)
        toast.success(t('docs.deleted'))
      } catch {
        toast.error(t('docs.deleteFailed'))
      }
    },
    [deletePage, t],
  )

  const setRowRef = useCallback((id: string, el: HTMLDivElement | null) => {
    if (el) rowRefs.current.set(id, el)
    else rowRefs.current.delete(id)
  }, [])

  return (
    <div className="overflow-hidden rounded-[14px] border bg-card">
      <div
        className={cn(
          GRID,
          'h-9 items-center border-b px-4 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground',
        )}
      >
        <span className="flex items-center gap-1.5">
          {t('docs.columns.title')}
          {parentIds.size > 0 && (
            <button
              type="button"
              onClick={() => setCollapsedIds(allCollapsed ? new Set() : new Set(parentIds))}
              aria-label={allCollapsed ? t('docs.expandAll') : t('docs.collapseAll')}
              title={allCollapsed ? t('docs.expandAll') : t('docs.collapseAll')}
              className="flex size-5 items-center justify-center rounded normal-case hover:bg-accent hover:text-foreground"
            >
              {allCollapsed ? <ChevronsUpDown className="size-3" /> : <ChevronsDownUp className="size-3" />}
            </button>
          )}
        </span>
        <span className="hidden sm:block">{t('docs.columns.inside')}</span>
        <span className="hidden sm:block">{t('docs.columns.updated')}</span>
        <span>{t('docs.columns.owner')}</span>
      </div>

      {rows.length === 0 ? (
        <p className="px-4 py-10 text-center text-sm text-muted-foreground">{t('docs.empty')}</p>
      ) : (
        <div role="tree" aria-label={t('docs.outlineAria')}>
          {rows.map(({ node, depth }) => (
            <OutlineRow
              key={node.page.id}
              node={node}
              depth={depth}
              expanded={!collapsedIds.has(node.page.id)}
              inside={inside.get(node.page.id) ?? 0}
              owner={memberByUser.get(node.page.createdBy)}
              flash={flashId === node.page.id}
              rowRef={setRowRef}
              onToggle={toggle}
              onOpen={onOpen}
              onCreateSubPage={onCreatePage}
              onDelete={handleDelete}
              draggingId={draggingId}
              onDragStart={setDraggingId}
              onDragEnd={() => setDraggingId(null)}
              onReorder={handleReorder}
              dropIndicator={dropIndicator}
              onDropIndicatorChange={setDropIndicator}
            />
          ))}
        </div>
      )}
    </div>
  )
}

interface OutlineRowProps {
  node: PageTreeNode
  depth: number
  expanded: boolean
  inside: number
  owner: MemberDocument | undefined
  flash: boolean
  rowRef: (id: string, el: HTMLDivElement | null) => void
  onToggle: (id: string) => void
  onOpen: (page: PageDocument) => void
  onCreateSubPage: (parentId: string) => void
  onDelete: (pageId: string) => Promise<void>
  draggingId: string | null
  onDragStart: (id: string) => void
  onDragEnd: () => void
  onReorder: (event: PageReorderEvent) => void
  dropIndicator: PageDropIndicator
  onDropIndicatorChange: (indicator: PageDropIndicator) => void
}

function OutlineRow({
  node,
  depth,
  expanded,
  inside,
  owner,
  flash,
  rowRef,
  onToggle,
  onOpen,
  onCreateSubPage,
  onDelete,
  draggingId,
  onDragStart,
  onDragEnd,
  onReorder,
  dropIndicator,
  onDropIndicatorChange,
}: OutlineRowProps) {
  const { t } = useTranslation('projects')
  const { smartTime } = useDateTimeFormatters()
  const { page } = node
  const localRef = useRef<HTMLDivElement>(null)
  const handleRef = useRef<HTMLButtonElement>(null)
  const hasChildren = node.children.length > 0
  const iconName = normalizeIconName(page.icon)
  const drop = dropIndicator?.id === page.id ? dropIndicator.position : null

  usePageRowDragDrop({
    pageId: page.id,
    rowRef: localRef,
    handleRef,
    dropIndicatorId: dropIndicator?.id ?? null,
    onDragStart,
    onDragEnd,
    onReorder,
    onDropIndicatorChange,
  })

  const ownerName = owner?.name ?? owner?.email ?? ''
  const initials = ownerName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join('')

  return (
    <div
      ref={(el) => {
        localRef.current = el
        rowRef(page.id, el)
      }}
      role="treeitem"
      aria-level={depth + 1}
      aria-expanded={hasChildren ? expanded : undefined}
      tabIndex={0}
      onClick={() => onOpen(page)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter') onOpen(page)
        else if (hasChildren && ((e.key === 'ArrowRight' && !expanded) || (e.key === 'ArrowLeft' && expanded))) {
          e.preventDefault()
          onToggle(page.id)
        }
      }}
      className={cn(
        GRID,
        'group relative h-10 cursor-pointer items-center border-b border-border/60 px-4 text-[13.5px] transition-colors last:border-b-0',
        'hover:bg-accent/40 focus-visible:bg-accent/40 focus-visible:outline-none',
        draggingId === page.id && 'opacity-40',
        drop === 'child' && 'bg-brand-tint/60 ring-1 ring-inset ring-brand/40',
        flash && 'bg-brand-tint',
      )}
    >
      {drop === 'before' && <span className="pointer-events-none absolute inset-x-3 -top-px h-0.5 rounded-full bg-brand" />}
      {drop === 'after' && <span className="pointer-events-none absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-brand" />}

      <div className="relative flex h-10 min-w-0 items-center gap-1.5" style={{ paddingLeft: depth * INDENT }}>
        {/* One guide per ancestor level, under that ancestor's chevron. */}
        {Array.from({ length: depth }, (_, level) => (
          <span
            key={level}
            aria-hidden
            className="absolute inset-y-0 w-px bg-border"
            style={{ left: level * INDENT + 6 }}
          />
        ))}
        <button
          type="button"
          ref={handleRef}
          onClick={(e) => e.stopPropagation()}
          aria-label={t('docs.dragHandle')}
          className="absolute flex h-5 w-3.5 items-center justify-center text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100 active:cursor-grabbing"
          style={{ left: depth * INDENT - ROW_PAD + 1 }}
        >
          <GripVertical className="size-3" />
        </button>
        <button
          type="button"
          tabIndex={-1}
          aria-hidden={!hasChildren}
          onClick={(e) => {
            e.stopPropagation()
            onToggle(page.id)
          }}
          className={cn(
            'flex size-[13px] shrink-0 items-center justify-center text-muted-foreground transition-transform',
            !hasChildren && 'invisible',
            expanded && 'rotate-90',
          )}
        >
          <ChevronRight className="size-[13px]" />
        </button>
        {iconName ? (
          <Icon name={iconName} className="size-3.5 shrink-0" />
        ) : (
          <FileText className="size-3.5 shrink-0 text-muted-foreground" />
        )}
        <span className={cn('truncate', depth === 0 && 'font-medium')}>{page.title || t('docs.untitled')}</span>

        <span className="ml-auto flex shrink-0 items-center gap-0.5 pl-2 opacity-0 transition-opacity focus-within:opacity-100 group-hover:opacity-100">
          {canCreateSubPage(page, depth) && (
            <button
              type="button"
              aria-label={t('docs.addSubpage')}
              title={t('docs.addSubpage')}
              onClick={(e) => {
                e.stopPropagation()
                onCreateSubPage(page.id)
              }}
              className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <FilePlus className="size-3.5" />
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('docs.options')}
                onClick={(e) => e.stopPropagation()}
                className="flex size-6 items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground"
              >
                <MoreHorizontal className="size-3.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-40" onClick={(e) => e.stopPropagation()}>
              <DropdownMenuItem className="text-destructive" onSelect={() => void onDelete(page.id)}>
                <Trash className="size-4" />
                {t('docs.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </span>
      </div>

      <span className="hidden text-xs text-muted-foreground sm:block">
        {inside > 0 ? t('docs.pagesInside', { count: inside }) : '—'}
      </span>
      <span className="hidden truncate text-xs text-muted-foreground sm:block">{smartTime(new Date(page.updatedOn))}</span>
      <span>
        {owner ? (
          <Avatar className="size-[18px]" title={ownerName}>
            <AvatarImage src={memberAvatarSrc(owner)} alt="" />
            <AvatarFallback className="text-[8px]">{initials}</AvatarFallback>
          </Avatar>
        ) : null}
      </span>
    </div>
  )
}
